<style lang="scss" scoped>
  @import "./storefront.scss";
</style>

<script>
import { CONSTANT, LOCALES } from '../../../utils/constant.js'
import { mapGetters } from 'vuex'
import sharedService from '../../../utils/shared-service'
import SdkSelection from './sdk-selection/sdk-selection'

export default {

  components: {
    SdkSelection
  },

  data () {
    return {
      constants: CONSTANT,
      isOtherLocale: false,
      otherLocale: this.value,
      localeValidationError: false,
      locales: LOCALES
    }
  },

  computed: {
    ...mapGetters(['quickCartProducts', 'currencySymbol']),

    currency: {
      get () {
        return this.$store.state.configuration.storefront.currency || 'USD'
      },
      set (value) {
        this.$store.commit('changeCurrency', value)
        this.$store.commit('setC2PCurrency', value)
        sharedService.updateCheckoutButton(false)
      }
    },

    locale: {
      get () {
        return this.$store.state.configuration.storefront.locale || 'en_US'
      },
      set (value) {
        this.$store.commit('changeLocale', value)
        sharedService.updateCheckoutButton(false)
      }
    }

  },

  watch: {
    locale () {
      this.selectLocale(this.locale)
    }
  },

  mounted () {
    this.selectLocale(this.locale)
  },

  methods: {

    /**
     * update changed item price in store
     * @param product
     */
    changeItemPrice (product) {
      this.$store.dispatch('changeItemPrice', product)
      sharedService.updateCheckoutButton(false)
    },

    /**
     * update changed item quantity in store
     * @param product
     * @param index
     */
    changeItemQuantity (product, index) {
      if (product.quantity < 1) {
        this.removeItem(index)
        return
      }
      this.$store.dispatch('changeItemQuantity', product)
      sharedService.updateCheckoutButton(false)
    },

    /**
     * update changes item in store
     * @param product
     * @param increase
     * @param index
     */
    changeItem (product, increase, index) {
      if (increase) {
        ++product.quantity
        this.changeItemQuantity(product, index)
      } else {
        if (product.quantity < 2) {
          this.removeItem(index)
          return
        }
        --product.quantity
        this.changeItemQuantity(product, index)
      }
      sharedService.updateCheckoutButton(false)
    },

    /**
     * remove item from store
     * @param index
     */
    removeItem (index) {
      this.$store.dispatch('removeItem', index)
      sharedService.updateCheckoutButton(false)
    },

    /**
     * add item in store
     */
    addItem () {
      this.$store.dispatch('incrementQuickCartItem')
      sharedService.updateCheckoutButton(false)
    },

    /**
     * select locale and check for Others
     * @param selectedLocale
     */
    selectLocale (selectedLocale) {
      this.localeValidationError = false
      if (selectedLocale === CONSTANT.CONFIG.OTHER_LOCALE) {
        this.isOtherLocale = true
      } else {
        this.isOtherLocale = false
        this.$store.commit('changeOtherLocale', '')
      }
      this.otherLocale = this.$store.getters.otherLocale || ''
    },

    /**
     * check preferred locale and update if valid
     * @param locale
     */
    checkValidLocale (locale) {
      sharedService.updateCheckoutButton(false)
      const validLocale = this.validateLocale(locale)
      if (validLocale) {
        this.$store.commit('changeOtherLocale', locale)
      } else {
        this.$store.commit('changeOtherLocale', '')
      }
    },

    /**
     * validate input preferred locale
     * @param preferredLocale
     * @return boolean
     */
    validateLocale (preferredLocale) {
      const pattern = /^[a-z]{2}_[A-Z]{2}$/
      if (!pattern.test(preferredLocale)) {
        this.localeValidationError = true
        return false
      } else {
        this.localeValidationError = false
        return true
      }
    }
  }
}
</script>
<template>
  <div id="configuration-storefront">
    <div class="card mb-4">
      <h5
        aria-controls="quickCart"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#quickCart"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{ $t('label.selectProduct') }}</span>
        <span class="collapsible-arrow pull-right cursor-pointer" />
      </h5>
      <div
        id="quickCart"
        class="card-body collapse show"
      >
        <div class="font-weight-bold mb-2">
          {{ $t('label.cartItems') }}
        </div>
        <div
          id="quickCartItems"
          class="row"
        >
          <div
            v-for="(product, index) in quickCartProducts"
            :key="product.productId"
            class="item"
            data-test="quick-cart-item"
          >
            <p class="config-label m-0">
              {{ $t('label.itemPrice') }}
            </p>
            <div
              class="quickCart-currency"
              data-test="item-currency"
              v-html="currencySymbol"
            />
            <input
              v-model="product.price"
              class="item-price"
              data-test="item-price"
              type="number"
              @change="changeItemPrice(product)"
            >
            <p class="config-label m-0">
              {{ $t('label.quantity') }}
            </p>
            <label>
              <input
                v-model="product.quantity"
                data-test="item-quantity"
                type="number"
                @change="changeItemQuantity(product, index)"
              >
            </label>
            <hr class="mb-2 mt-1">
            <button
              id="add-item"
              class="btn-sm rounded-pill mr-1"
              data-test="add-quick-cart-item"
              type="button"
              @click="changeItem(product, true)"
            >
              +
            </button>
            <button
              id="decrease-item"
              class="btn-sm rounded-pill mr-1"
              data-test="decrease-quick-cart-item"
              type="button"
              @click="changeItem(product, false, index)"
            >
              -
            </button>
            <button
              id="remove-item"
              class="pull-right btn-sm btn-danger rounded-pill"
              data-test="remove-quick-cart-item"
              type="button"
              @click="removeItem(index)"
            >
              &times;
            </button>
          </div>
          <button
            id="add-cart-item"
            class="item add"
            @click="addItem()"
          >
            <i class="cart-add-item fa fa-plus-circle fa-2x" />
            <label class="config-label">{{ $t('label.addCartItems') }}</label>
          </button>
        </div>
        <div class="form-group row mt-3">
          <label
            class="col-sm-3 col-form-label"
            for="currencySelected"
          >{{ $t('label.currency') }}</label>
          <div class="col-sm-9">
            <select
              id="currencySelected"
              v-model="currency"
              class="form-control"
              data-test="currency"
            >
              <option
                v-for="curr in constants.CONFIG.CURRENCIES"
                :key="curr.code"
                :value="curr.code"
              >
                {{ curr.code }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="card mb-4">
      <h5
        aria-controls="storefront"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#storefront"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{ $t('label.selectLocale') }}</span>
        <span class="collapsible-arrow pull-right cursor-pointer" />
      </h5>
      <div
        id="storefront"
        class="card-body collapse"
      >
        <div class="form-group row">
          <label
            class="col-sm-3 col-form-label"
            for="localeSelected"
          >{{ $t('label.locale') }}</label>
          <div class="col-sm-9">
            <select
              id="localeSelected"
              v-model="locale"
              class="form-control"
              data-test="locale"
              @change="selectLocale(locale)"
            >
              <option
                v-for="localeObj in locales"
                :key="localeObj.label"
                :disabled="localeObj.separator"
                :value="localeObj.value"
              >
                {{ localeObj.label }}
              </option>
            </select>
          </div>
          <div class="col-sm-9 col-form-label ml-auto mt-2">
            <input
              v-if="isOtherLocale"
              id="preferredLocale"
              v-model.trim="otherLocale"
              :class="{'border-danger': localeValidationError}"
              class="form-control float-left"
              data-test="preferredLocale"
              placeholder="Preferred Locale"
              type="text"
              @blur="checkValidLocale(otherLocale)"
            >
            <p
              v-if="localeValidationError"
              id="localeValidationError"
              class="text-danger control-label mt-2"
            >
              {{ $t('errorMessages.invalidLocale') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <sdk-selection />
  </div>
</template>
